import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { APP_ROUTES_CONFIG, MODULE_LIST, ModuleType } from '@contracts';
import { AuthService, I18nService, ModuleStore, SessionStore } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { interval, map, startWith } from 'rxjs';
import { FullscreenService } from '../../service/full-screen.service';
import { toSignal } from '@angular/core/rxjs-interop';

interface Languages {
  value: string;
  viewValue: string;
}
const ENTERPRISE_COLORS = [
  '#6200ea',
  '#2962ff',
  '#00c853',
  '#f50057',
  '#ff6d00',
];
@Component({
  selector: 'app-top-nav',
  templateUrl: './top-nav.component.html',
  styleUrl: './top-nav.component.scss',
})
export class TopNavComponent {
  @Output() clickEvent = new EventEmitter();
  email: string = 'Admin';
  userRole: string = 'Xten Admin';

  private moduleStore = inject(ModuleStore);
  private sessionStore = inject(SessionStore);
  private config = inject(APP_ROUTES_CONFIG);
  private fullscreenService = inject(FullscreenService);

  displayAvatar: string | null = this.sessionStore.getAvatar();
  avatarFallbackChar: string = '';
  avatarBackgroundColor: string = '#757575';

  moduleMenus = toSignal(this.config.module_menu, { initialValue: [] });
  logo = this.config.logoPath;
  dateNow$ = interval(1000).pipe(
    startWith(0),
    map(() => new Date()),
  );

  onModuleClick(item: MODULE_LIST) {
    if (item.action) item.action();
  }

  get selectedLanguageLabel(): string {
    return (
      this.Languages.find((v) => v.value === this.translate.currentLang)
        ?.viewValue ?? ''
    );
  }

  get isFullScreenNow(): boolean {
    return this.fullscreenService.isFullscreen;
  }

  onToggleFullscreen() {
    this.fullscreenService.toggle();
  }

  @HostListener('document:fullscrenchange', ['$event'])
  @HostListener('document:webkitfullscreenchange', ['$event'])
  @HostListener('document:mozfullscreenchange', ['$event'])
  @HostListener('document:MSFullscreenChange', ['$event'])
  onFullscreenChange(event: Event) {}

  Languages: Languages[] = [
    { value: 'th', viewValue: 'ไทย' },
    { value: 'en', viewValue: 'English' },
  ];

  constructor(
    private router: Router,
    public translate: TranslateService,
    private authService: AuthService,
    private i18n: I18nService,
  ) {
    this.loadUserFromStorage();
  }

  onSelectLanguage(value: any) {
    this.i18n.switchLang(value);
  }

  logout() {
    this.authService.clearSession();
    this.router.navigate(['/login']);
  }

  private loadUserFromStorage() {
    const user = this.sessionStore.getUserInfo();
    if (!user) return;

    try {
      this.email = user.email;
      this.userRole = user.role ?? '';

      const getRoleInitials = (roleName: string) => {
        if (!roleName) return '';
        return roleName
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase())
          .join('');
      };
      this.avatarFallbackChar =
        getRoleInitials(this.userRole) || this.email.charAt(0).toUpperCase();
      this.avatarBackgroundColor = this.getBackgroundColorForUser(user.role);
    } catch (e) {
      console.warn('Invalid user in localStorage');
      this.avatarFallbackChar = 'E';
    }
  }

  onToggleModule(checked: boolean) {
    const module: ModuleType = checked ? 'EMS' : 'RMS';

    this.moduleStore.setModule(module);

    this.router.navigateByUrl(this.config.defaultRoutes[module], {
      replaceUrl: true,
    });
  }

  private getBackgroundColorForUser(name: string): string {
    if (!name) return '#757575';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % ENTERPRISE_COLORS.length;
    return ENTERPRISE_COLORS[index];
  }

  public handleImageError() {
    this.displayAvatar = null;
  }
  get module() {
    return this.currentModule;
  }
  get currentModule(): string | null {
    return this.moduleStore.module();
  }

  get currentModuleLabel(): string {
    return (
      this.moduleMenus().find((m) => m.id === this.currentModule)?.label || ''
    );
  }

  get currentModuleIcon(): string {
    const current = this.sessionStore.getModule();
    return this.moduleMenus().find((m) => m.id === current)?.icon || '';
  }

  get favoriteList(): string[] {
    return this.sessionStore.getFavoriteModules();
  }

  get favoriteModulesData(): MODULE_LIST[] {
    return this.moduleMenus().filter((m) =>
      this.favoriteList.includes(m.label),
    );
  }

  toggleFavorite(event: Event, moduleLabel: string) {
    event.stopPropagation();
    if (this.favoriteList.includes(moduleLabel))
      this.sessionStore.removeFavorite(moduleLabel);
    else this.sessionStore.addFavorite(moduleLabel);
  }
}
